import { useCallback } from 'react';
import { useFilters } from '../../../../components/Elements/Filters/FilterContext';
import { navigateWithFilters, buildNavigationParams } from '../utils/filterNavigation';

/**
 * Hook for handling navigation from chart items or view data button
 * @param {Object} config - The chart configuration containing viewData and itemNavigation settings
 * @param {Object} drilldownState - The current drilldown state from useReport/useDrilldown
 * @returns {Object} Navigation handler functions
 */
export const useItemNavigation = (config, drilldownState) => {
  const { filterValues, siteAppId } = useFilters();
  const { drilldownHistory, isDrilledDown, config: activeConfig } = drilldownState;
  
  // Modernized navigation logic using Filter Framework URL building
  const navigateToUrl = useCallback((targetUrl, dataPoint = null) => {
    if (!targetUrl) return;
    
    // Build additional parameters from drilldown history and data point, with URL-aware mapping
    const additionalParams = buildNavigationParams(drilldownHistory, dataPoint, targetUrl);
    
    // Use the new URL-based navigation utility
    navigateWithFilters(targetUrl, filterValues, additionalParams, siteAppId);
  }, [filterValues, drilldownHistory, siteAppId]);

  // For ViewData button clicks - reuses ViewDataButton logic
  const handleViewDataClick = useCallback(() => {
    // Resolve the viewData URL (can be string or function)
    const viewDataUrl = typeof config.viewData === 'function' 
      ? config.viewData(drilldownState, {}, activeConfig) 
      : config.viewData;
    
    navigateToUrl(viewDataUrl);
  }, [config, navigateToUrl]);
  
  // For chart item clicks - simplified with new URL-based approach
  const handleItemClick = useCallback((dataPoint) => {
    // Check if this is an asset-level item (depth === 'A') or status-level item (depth === 'S')
    const isAssetLevel = dataPoint && dataPoint.depth === 'A';
    const isStatusLevel = dataPoint && dataPoint.depth === 'S';
    
    // Special handling for specific depth items
    if ((isAssetLevel || isStatusLevel) && config.viewData) {
      // Use the viewData URL for navigation
      const targetUrl = typeof config.viewData === 'function' 
        ? config.viewData(dataPoint) 
        : config.viewData;
      
      if (targetUrl) {
        navigateToUrl(targetUrl, dataPoint);
        return true; // Handled navigation
      }
      
      return false;
    }
    
    // Only handle navigation via standard config if we're at the bottom level
    if (!dataPoint || !activeConfig || activeConfig.drilldown) {
      return false; // Not handled, let regular drilldown handle it
    }
    
    // Check if item navigation is enabled via config
    const itemNavigation = activeConfig.itemNavigation;
    if (!itemNavigation) {
      return false; // Not handled, no navigation configured
    }
    
    // Determine the target URL based on configuration
    let targetUrl;
    
    if (typeof itemNavigation === 'function') {
      // Function can return a URL string or false to disable navigation
      targetUrl = itemNavigation(dataPoint);
      
      if (targetUrl === false) {
        return false; // Function explicitly disabled navigation for this item
      }
    } else if (itemNavigation === true) {
      // Use the same URL as viewData if itemNavigation is just true
      targetUrl = typeof config.viewData === 'function' 
        ? config.viewData(dataPoint) 
        : config.viewData;
    } else if (typeof itemNavigation === 'string') {
      // Direct URL string
      targetUrl = itemNavigation;
    }
    
    if (targetUrl) {
      navigateToUrl(targetUrl, dataPoint);
      return true; // Handled navigation
    }
    
    return false; // Not handled
  }, [config, activeConfig, navigateToUrl, isDrilledDown, drilldownHistory]);

  return { handleViewDataClick, handleItemClick };
}; 
